import React, {useCallback, useState} from "react";
import Button from "../../../../../components/base/button/button";
import {Card} from "@mui/material";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from '../../../../../components/form';
import {usePartnerInformation, usePartnerInformationController} from "../../../../../hooks/use-partner-information";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import Grid2 from "@mui/material/Unstable_Grid2";
import EnvService from "../../../../../../core/services/env";


const EditPartnerAddressInformationSection = () => {
    const partnerInformation = usePartnerInformation();
    const {setCachedPartnerInformation} = usePartnerInformationController();
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();

    const schema = React.useMemo(() => Yup.object().shape({
        country: Yup.string().trim().nullable().required(ValidateMessages.required),
        province: Yup.string().trim().nullable().required(ValidateMessages.required),
        fullAddress: Yup.string().trim().nullable().required(ValidateMessages.required),
        postalCode: Yup.string().trim().nullable().required(ValidateMessages.required),
        city: Yup.string().trim().nullable().required(ValidateMessages.required),
    }), []);

    const validate = React.useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = React.useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = React.useMemo(() => ({
        country: partnerInformation?.country ?? "",
        province: partnerInformation?.province ?? "",
        fullAddress: partnerInformation?.fullAddress ?? "",
        postalCode: partnerInformation?.postalCode ?? "",
        city: partnerInformation?.city ?? "",
    }), [partnerInformation]);

    /**
     * Handles submission of the profile information changes:
     * Calls the api regarding this action
     * if the api call was not successful, shows an error dialog, otherwise, sets the current information to the caching service
     * @param values
     */
    const onSubmit = useCallback(async (values) => {
        setSubmitting(true);
        const response = await Api.updatePartnerBasicInfo({
            country: values?.country ?? "",
            province: values?.province ?? "",
            fullAddress: values?.fullAddress ?? "",
            postalCode: values?.postalCode ?? "",
            city: values?.city ?? "",
            updatedProperties: ["Country", "Province", "FullAddress", "PostalCode", "City"],
        });
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            setCachedPartnerInformation(prevState => ({
                ...prevState,
                country: values?.country ?? "",
                province: values?.province ?? "",
                fullAddress: values?.fullAddress ?? "",
                postalCode: values?.postalCode ?? "",
                city: values?.city ?? "",
            }));
        }
    }, [isMounted, setCachedPartnerInformation]);

    return (
        <>
            <Card variant={'outlined'} sx={{padding: 3}}>
                {
                    !partnerInformation &&
                    <div className={'loading current-operator-profile'}>
                        <div/>
                    </div>
                }
                {
                    !!partnerInformation &&
                    <Form
                        formProps={{className: '100%'}}
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                    >
                        {({pristine, values}) => (
                            <Grid2 container spacing={3}>
                                <Grid2 xs={12}>
                                    <p className={'text-lg font-weight-semi-bold text-color-1'}>
                                        Address Information
                                    </p>
                                </Grid2>
                                <Grid2 xs={6}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={'Country'}
                                        name={'country'}
                                        required={required.country}
                                    />
                                </Grid2>
                                <Grid2 xs={6}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={!values.country ? 'Province / State' : values.country === EnvService.USATitle ? "State" : 'Province'}
                                        name={'province'}
                                        required={required.province}
                                    />
                                </Grid2>

                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={'Street Address'}
                                        name={'fullAddress'}
                                        required={required.fullAddress}
                                    />
                                </Grid2>
                                <Grid2 xs={8}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={'City'}
                                        name={'city'}
                                        required={required.city}
                                    />
                                </Grid2>
                                <Grid2 xs={4}>
                                    <MuiRFF.TextField
                                        disabled={submitting}
                                        label={!values.country ? 'Postal/Zip Code' : values.country === EnvService.USATitle ? "Zip Code" : 'Postal Code'}
                                        name={'postalCode'}
                                        required={required.postalCode}
                                    />
                                </Grid2>
                                <Grid2 xs={12} sx={{marginBlockStart: 2}}>
                                    <Button
                                        disabled={!!submitting || pristine}
                                        type={'submit'}
                                        variant={'contained'}
                                        color={'primary'}
                                        size={'medium'}
                                        sx={{minWidth: 200, marginInline: 'auto', display: 'flex'}}
                                    >
                                        {submitting ? 'Submitting...' : 'Save'}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        )}
                    </Form>
                }
            </Card>
        </>
    );
}

export default EditPartnerAddressInformationSection;
